import { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { ROUTES } from "../classes/constants/routeConstants";
import { useAppContext } from "../classes/contexts/AppContext";
import { PatientProvider } from "../classes/contexts/PatientContext";
import { useUser } from "../classes/contexts/UserContext";
import { SidebarPosition } from "../classes/enums/SideBarsEnum";
import Popup from "../components/popup/Popup";
import SideBar from "../components/sideBar/SideBar";
import TopBar from "../components/topBar/TopBar";
import { routes } from "../config/routeConfig";
import { useAuth } from "../hooks/useAuth";
import useInactivityTimer from "../hooks/useInactivityTimer";
import OnboardingPopup from "../modules/common/onboarding/OnboardingPopup";
import "./MainLayout.css";

const AppLayout = ({ children }) => {
  const { showRightSidebar, showLeftSidebar } = useAppContext();

  return (
    <div className="App-page">
      <div className="App-topBar">
        <TopBar />
      </div>
      {showLeftSidebar && (
        <div className="App-sidebar">
          <SideBar alignment={SidebarPosition.LEFT} isCollapsible={true} />
        </div>
      )}
      <div className="App-placeholder route-transition">{children}</div>
      {showRightSidebar && (
        <div className="App-rightsidebar">
          <SideBar alignment={SidebarPosition.RIGHT} isCollapsible={false} />
        </div>
      )}
    </div>
  );
};

const RouteWithPatientContext = ({ path, element }) => {
  const location = useLocation();
  const { showPopup } = useAppContext();
  const { isAuthenticated, loading, logout } = useAuth();
  const { onboardingCompleted, preferencesLoading, isClinicianReady } =
    useUser();

  // Enable inactivity timer: logout after 10 minutes of inactivity (only when authenticated)
  useInactivityTimer(10, () => {
    if (isAuthenticated) {
      logout();
    }
  });

  useEffect(() => {
    // Only show onboarding popup if:
    // 1. User is authenticated
    // 2. Data is loaded (not loading)
    // 3. Clinician is ready (has valid ID and is not being created/loaded)
    // 4. Onboarding is explicitly false (not null which means unknown state)
    if (
      isAuthenticated &&
      !loading &&
      !preferencesLoading &&
      isClinicianReady &&
      onboardingCompleted === false
    ) {
      showPopup(<OnboardingPopup />);
    }
  }, [
    isAuthenticated,
    loading,
    preferencesLoading,
    isClinicianReady,
    onboardingCompleted,
    showPopup,
  ]);

  const routeElement =
    path === ROUTES.LOGIN || path === ROUTES.USER_VALIDATION ? (
      element
    ) : (
      <AppLayout key={location.pathname}>{element}</AppLayout>
    );

  return (
    <PatientProvider>
      {routeElement}
      <Popup />
    </PatientProvider>
  );
};

const MainLayout = () => {
  return (
    <Routes>
      {routes.map(({ path, element }) => (
        <Route
          key={path}
          path={path}
          element={<RouteWithPatientContext path={path} element={element} />}
        />
      ))}
    </Routes>
  );
};

export default MainLayout;
